import React from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FaUsers,
  FaMapMarkerAlt,
  FaComments,
  FaChartBar,
  FaHistory,
} from "react-icons/fa";

const FCDashboard = () => {
  const { user } = useAuth();

  const container = {
    width: "100%",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#f8fafc",
    padding: "0",
    margin: "0",
  };

  const contentWrapper = {
    width: "100%",
    maxWidth: "100%",
    padding: "15px",
    boxSizing: "border-box",
  };

  const header = {
    marginBottom: "25px",
    padding: "0 5px",
  };

  const title = {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "8px",
    lineHeight: "1.3",
  };

  const subtitle = {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
    margin: "0",
  };

  const statGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
    width: "100%",
  };

  const statCard = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: "20px",
    border: "1px solid #e5e7eb",
    width: "100%",
    boxSizing: "border-box",
    minHeight: "140px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  const statTitle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#2563eb",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "10px",
  };

  const statValue = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "6px",
  };

  const statTrend = {
    fontSize: "12px",
    color: "#22c55e",
    fontWeight: "500",
  };

  const mainGrid = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%",
  };

  const card = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    boxSizing: "border-box",
  };

  const sectionTitle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "18px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f1f5f9",
  };

  const sectionIcon = {
    color: "#2563eb",
    fontSize: "16px",
  };

  const performanceItem = {
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexDirection: "column",
    gap: "8px",
  };

  const performanceDetails = {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const performanceName = {
    fontSize: "15px",
    fontWeight: "600",
    color: "#111827",
  };

  const performanceMembers = {
    fontSize: "13px",
    color: "#3b82f6",
    textAlign: "right",
  };

  const performanceChange = {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "8px",
    borderTop: "1px dashed #e5e7eb",
  };

  const performanceChangeLabel = {
    fontSize: "12px",
    color: "#6b7280",
  };

  const activityItem = {
    display: "flex",
    gap: "12px",
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    alignItems: "flex-start",
  };

  const activityIcon = {
    width: "8px",
    height: "8px",
    backgroundColor: "#2563eb",
    borderRadius: "50%",
    marginTop: "8px",
    flexShrink: 0,
  };

  const activityDetails = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const activityTitle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
    lineHeight: "1.4",
  };

  const activitySub = {
    fontSize: "13px",
    color: "#3b82f6",
    fontWeight: "500",
  };

  const activityTime = {
    fontSize: "12px",
    color: "#9ca3af",
    alignSelf: "flex-start",
    backgroundColor: "#f8fafc",
    padding: "4px 8px",
    borderRadius: "6px",
    marginTop: "4px",
  };

  // Responsive styles
  const responsiveStyles = `
    @media (min-width: 768px) {
      .dashboard-content-wrapper {
        padding: 25px;
        max-width: 1200px;
        margin: 0 auto;
      }
      
      .dashboard-stat-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-bottom: 35px;
      }
      
      .dashboard-stat-card {
        padding: 25px;
        minHeight: 160px;
      }
      
      .dashboard-stat-value {
        font-size: 28px;
      }
      
      .dashboard-main-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 25px;
      }
      
      .dashboard-card {
        padding: 25px;
      }
      
      .dashboard-performance-item {
        flex-direction: row;
        align-items: center;
        gap: 0;
      }
      
      .dashboard-performance-details {
        width: auto;
        flex: 1;
      }
      
      .dashboard-performance-members {
        text-align: left;
        margin-top: 4px;
      }
      
      .dashboard-performance-change {
        width: auto;
        border-top: none;
        padding-top: 0;
        justify-content: flex-end;
        flex-direction: column;
        align-items: flex-end;
        min-width: 100px;
      }
      
      .dashboard-activity-details {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
      
      .dashboard-activity-time {
        align-self: center;
        margin-top: 0;
      }
    }
    
    @media (min-width: 1024px) {
      .dashboard-content-wrapper {
        padding: 30px 40px;
      }
      
      .dashboard-title {
        font-size: 24px;
      }
      
      .dashboard-subtitle {
        font-size: 15px;
      }
    }
    
    @media (max-width: 767px) {
      .dashboard-stat-grid {
        grid-templateColumns: 1fr;
        gap: 12px;
      }
    }
    
    @media (max-width: 480px) {
      .dashboard-stat-grid {
        grid-templateColumns: 1fr;
      }
      
      .dashboard-stat-card {
        padding: 15px;
        min-height: 130px;
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={container} className="dashboard-container">
        <div style={contentWrapper} className="dashboard-content-wrapper">
          {/* Header */}
          <div style={header}>
            <h2 style={title} className="dashboard-title">Federal Constituency Administration Dashboard</h2>
            <p style={subtitle} className="dashboard-subtitle">
              Welcome, {user?.full_name || 'Federal Constituency Administrator'}! Manage your federal constituency operations
            </p>
          </div>

          {/* Stats Section */}
          <div style={statGrid} className="dashboard-stat-grid">
            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Constituency Members</span>
                <FaUsers />
              </div>
              <div style={statValue} className="dashboard-stat-value">18,927</div>
              <div style={statTrend}>+5.4% from last month</div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Active LGAs</span>
                <FaMapMarkerAlt />
              </div>
              <div style={statValue} className="dashboard-stat-value">8</div>
              <div style={statTrend}>+1 from last month</div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Active Chats</span>
                <FaComments />
              </div>
              <div style={statValue} className="dashboard-stat-value">523</div>
              <div style={statTrend}>+7.2% from last month</div>
            </div>
          </div>

          {/* Main Grid - Only 2 cards now */}
          <div style={mainGrid} className="dashboard-main-grid">
            {/* LGAs Performance */}
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaChartBar style={sectionIcon} />
                Local Governments Performance
              </h3>

              {[
                { name: "Lagos Island LGA", members: "6,847", change: "+6.1%" },
                { name: "Lagos Mainland LGA", members: "5,234", change: "+8.3%" },
                { name: "Surulere LGA", members: "4,892", change: "+4.7%" },
                { name: "Ikeja LGA", members: "3,945", change: "+10.2%" },
                { name: "Alimosho LGA", members: "3,521", change: "+3.9%" },
              ].map((unit, index) => (
                <div key={index} style={performanceItem} className="dashboard-performance-item">
                  <div style={performanceDetails} className="dashboard-performance-details">
                    <div style={performanceName}>{unit.name}</div>
                    <div style={{...performanceMembers, textAlign: "right"}} className="dashboard-performance-members">
                      {unit.members}
                    </div>
                  </div>
                  <div style={performanceChange} className="dashboard-performance-change">
                    <div style={{color: "#22c55e", fontWeight: "600", fontSize: "14px"}}>
                      {unit.change}
                    </div>
                    <div style={performanceChangeLabel}>vs last month</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Activity Feed */}
            <div style={card} className="dashboard-card dashboard-activity-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaHistory style={sectionIcon} />
                Recent Activity
              </h3>

              {[
                { title: "New members registered", sub: "Lagos Island LGA", time: "8 minutes ago" },
                { title: "Constituency broadcast sent", sub: "Federal Announcement", time: "30 minutes ago" },
                { title: "LGA performance report", sub: "Weekly Analytics", time: "1 hour ago" },
                { title: "Group chat created", sub: "Constituency Coordination", time: "3 hours ago" },
                { title: "Member verification batch", sub: "32 members verified", time: "5 hours ago" },
              ].map((activity, index) => (
                <div key={index} style={activityItem} className="dashboard-activity-item">
                  <div style={activityIcon}></div>
                  <div style={activityDetails} className="dashboard-activity-details">
                    <div style={activityTitle}>{activity.title}</div>
                    <div style={activitySub}>{activity.sub}</div>
                    <div style={activityTime} className="dashboard-activity-time">{activity.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FCDashboard;